import React, {useState} from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import PrintoutDocumentRenderer from "../../PrintoutDocumentRenderer";
import PrintPreviewPageShell from "../../shell/PrintPreviewPageShell";
import PrintPreviewPrintButton from "../../shell/PrintPreviewPrintButton";
import PrintPreviewSidebarContent from "../../shell/PrintPreviewSidebarContent";
import {
    getPrintoutLayoutOptions,
    getPrintoutLayoutSelectionValue,
    parsePrintoutLayoutSelection,
    type PrintoutLayoutSelection,
} from "../../shell/printoutLayoutSelection";
import {usePrintoutDocumentVariables} from "../../shell/printoutDocumentVariables";
import {
    EMPTY_PRINT_VALUE,
    formatPrintNativeLanguage,
    formatPrintStatus,
} from "../../shell/printPreviewFormatters";
import {resolvePrintoutVariable} from "../../../variables/printoutVariableResolver";
import {useReservationPrintData} from "./useReservationConfirmationPrintData";
import {LoadingButton} from "@mui/lab";

const getPrintoutTypeLabel = (type?: Printout["type"]) => {
    switch (type) {
        case PrintoutType.RESERVATION_CONFIRMATION:
            return "Reservation confirmation";
        case PrintoutType.RESERVATION_CANCELLATION:
            return "Reservation cancellation";
        default:
            return formatPrintStatus(type);
    }
};

const ReservationConfirmationPrintPage = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const [selectedLayout, setSelectedLayout] = useState<PrintoutLayoutSelection | undefined>();
    const printData = useReservationPrintData(id, selectedLayout?.printoutId, selectedLayout?.language);

    const selectedPrintout = printData.selectedPrintout;
    const printLanguage = printData.language;
    const guestLanguage = printData.profile?.languageCode;
    const selectedPrintoutLanguage = selectedPrintout
        ? formatPrintNativeLanguage(printLanguage)
        : undefined;
    const documentVariables = usePrintoutDocumentVariables(selectedPrintout, printData.context, printLanguage);

    const resolvedValue = (key: string) => (
        printData.context
            ? resolvePrintoutVariable(key, printData.context)?.displayValue || EMPTY_PRINT_VALUE
            : EMPTY_PRINT_VALUE
    );

    const reservationRows = [
        {label: t("Code"), value: resolvedValue("resCode")},
        {label: t("Status"), value: formatPrintStatus(printData.reservation?.status)},
        {label: t("Start"), value: resolvedValue("resStartDate")},
        {label: t("End"), value: resolvedValue("resEndDate")},
        {label: t("Guests"), value: resolvedValue("resGuestCount")},
    ];

    const guestRows = [
        {label: t("Guest"), value: resolvedValue("guestFullName")},
        {label: t("Email"), value: resolvedValue("guestEmail")},
        {label: t("Phone"), value: resolvedValue("guestPhone")},
    ];

    // Show only the stay fields meaningful to the reservation's sector.
    const stayRows = printData.sector?.sectorType === SectorType.SPA
        ? [
            {label: t("Treatment"), value: resolvedValue("serviceName")},
            {label: t("Therapist"), value: resolvedValue("resStaffNames")},
            {label: t("Location"), value: resolvedValue("resFacilityNames")},
            {label: t("Duration"), value: resolvedValue("resDuration")},
        ]
        : [
            {label: t("Service"), value: resolvedValue("serviceName")},
            {label: t("Resources"), value: resolvedValue("resResourceNames")},
            {label: t("Duration"), value: resolvedValue("resDuration")},
        ];

    const closePreview = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        window.close();
    };

    return (
        <PrintPreviewPageShell
            actions={(
                <PrintPreviewPrintButton
                    disabled={!selectedPrintout || !printData.context || printData.isLoading || Boolean(printData.errorMessage)}
                    onClick={() => window.print()}
                />
            )}
            sidebar={(
                <PrintPreviewSidebarContent
                    alert={guestLanguage ? (
                        <>
                            {t("Guest language")}: <strong>{formatPrintNativeLanguage(guestLanguage)}</strong>
                        </>
                    ) : undefined}
                    onClose={closePreview}
                    selects={[
                        {
                            id: "reservation-printout-layout",
                            disabled: printData.isLoading || !printData.printouts.length,
                            emptyLabel: t("No reservation printouts"),
                            label: t("Printout"),
                            onChange: value => setSelectedLayout(parsePrintoutLayoutSelection(value)),
                            options: getPrintoutLayoutOptions(
                                printData.printouts,
                                formatPrintNativeLanguage,
                                t("Untitled printout"),
                            ),
                            value: getPrintoutLayoutSelectionValue(selectedPrintout, printLanguage),
                        },
                    ]}
                    tables={[
                        {rows: reservationRows},
                        {rows: guestRows},
                        {rows: stayRows},
                    ]}
                    printoutInfo={selectedPrintout ? {
                        language: selectedPrintoutLanguage,
                        type: t(getPrintoutTypeLabel(selectedPrintout.type)),
                    } : undefined}
                    title={t("Reservation Print")}
                    variables={documentVariables}
                />
            )}
        >
            {printData.isLoading && (
                <Stack alignItems="center" justifyContent="center" minHeight="100vh" spacing={2}>
                    <CircularProgress/>
                    <Typography color="text.secondary">
                        {t("Loading reservation print preview")}
                    </Typography>
                </Stack>
            )}

            {!printData.isLoading && printData.errorMessage && (
                <Box maxWidth={720} py={4} width="100%">
                    <Alert severity="error">
                        {printData.errorMessage}
                    </Alert>
                </Box>
            )}

            {!printData.isLoading && !printData.errorMessage && !selectedPrintout && (
                <Box maxWidth={720} py={4} width="100%">
                    <Alert
                        severity="info"
                        action={printData.createDefaultPrintout && (
                            <LoadingButton
                                size="small"
                                variant="contained"
                                loading={printData.creatingDefaultPrintout}
                                onClick={printData.createDefaultPrintout}
                            >
                                {t("Create printout")}
                            </LoadingButton>
                        )}
                    >
                        {printData.createDefaultPrintout
                            ? t("No reservation printout applies here yet. Create one from the standard layout - it is saved to your printouts, prints straight away, and can be edited under Settings > Printouts.")
                            : t("No reservation printout was found.")}
                    </Alert>

                    {printData.createDefaultPrintoutError && (
                        <Alert severity="error" sx={{mt: 1}}>
                            {printData.createDefaultPrintoutError}
                        </Alert>
                    )}
                </Box>
            )}

            {!printData.isLoading && !printData.errorMessage && selectedPrintout && printData.context && (
                <PrintoutDocumentRenderer
                    context={printData.context}
                    language={printLanguage}
                    printout={selectedPrintout}
                />
            )}
        </PrintPreviewPageShell>
    );
};

export default ReservationConfirmationPrintPage;
